// Pending async export/import jobs that have been "backgrounded" — a modal was
// closed (or timed out) before its backend job finished. BackgroundJobWatcher
// polls these and pops a completion toast + lights the header badge. Persisted
// to localStorage so a page reload mid-job resumes the watch.
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type PendingJobKind = "export" | "import";

// Which export list the BackgroundJobWatcher should poll this job against. Each
// export master owns its own request table + list endpoint, so an export job
// must say which one it belongs to. Absent = "norms" (backward-compatible with
// jobs persisted before manpower norms existed).
export type ExportListKind =
  | "norms"
  | "manpower"
  | "machinery"
  | "equipment-rates"
  | "deployment-norms";

export interface PendingJob {
  /** `${kind}-${reportId}` — stable dedupe/removal key. */
  jobId: string;
  kind: PendingJobKind;
  reportId: number;
  /** Human label for the toast, e.g. "Norms Master export" / "WBS rows import". */
  label: string;
  /** Route the toast's View/Download action links to (Exports / Imports list). */
  historyPath: string;
  /** Export jobs only: which export list endpoint to poll. Defaults to "norms". */
  listKind?: ExportListKind;
  projectId?: number | null;
  /** epoch ms — used to prune stale entries (>24h). */
  createdAt: number;
}

interface PendingJobsState {
  jobs: PendingJob[];
}

const STORAGE_KEY = "pms_pending_jobs";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // prune anything older than 24h

const isFresh = (job: PendingJob): boolean =>
  typeof job?.createdAt === "number" &&
  Date.now() - job.createdAt < MAX_AGE_MS;

const loadFromStorage = (): PendingJob[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // prune stale entries on hydrate
    return parsed.filter(
      (job: any) => job && typeof job.jobId === "string" && isFresh(job)
    );
  } catch {
    return [];
  }
};

const persist = (jobs: PendingJob[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch {
    // storage full / unavailable — the watcher still works for this session
  }
};

const initialState: PendingJobsState = {
  jobs: loadFromStorage(),
};

const pendingJobsSlice = createSlice({
  name: "pendingJobs",
  initialState,
  reducers: {
    addPendingJob: (state, action: PayloadAction<PendingJob>) => {
      const job = action.payload;
      // dedupe by jobId (re-registering the same job is a no-op update)
      const existing = state.jobs.findIndex((j) => j.jobId === job.jobId);
      if (existing >= 0) {
        state.jobs[existing] = job;
      } else {
        state.jobs.push(job);
      }
      persist(state.jobs);
    },
    removePendingJob: (state, action: PayloadAction<string>) => {
      state.jobs = state.jobs.filter((j) => j.jobId !== action.payload);
      persist(state.jobs);
    },
    clearPendingJobs: (state) => {
      state.jobs = [];
      persist(state.jobs);
    },
  },
});

export const { addPendingJob, removePendingJob, clearPendingJobs } =
  pendingJobsSlice.actions;

export default pendingJobsSlice.reducer;
